import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { PageHero, Section, CTA, Breadcrumbs, Prose } from "@/components/site/Sections";

const TITLE = "Sak-Electrics priser for privat | Elektriker-Elektriker";
const DESCRIPTION =
  "Se vores faste, lave og gennemskuelige timepriser for privatkunder. Priser inkl. moms. Døgnvagt til rådighed.";

export const Route = createFileRoute("/priser-privat")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "/priser-privat" },
    ],
    links: [{ rel: "canonical", href: "https://elektriker-elektriker.dk/priser-privat" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Elektrikerydelser for privatkunder",
          provider: { "@type": "Electrician", name: "Elektriker-Elektriker ApS", telephone: "+4528700028" },
          areaServed: { "@type": "AdministrativeArea", name: "Storkøbenhavn" },
          offers: [
            { "@type": "Offer", name: "Timepris 7:00–15:00 hverdage", price: "868.75", priceCurrency: "DKK", priceSpecification: { "@type": "UnitPriceSpecification", price: "868.75", priceCurrency: "DKK", unitText: "HUR" } },
            { "@type": "Offer", name: "Timepris 15:00–22:00 hverdage", price: "1368.75", priceCurrency: "DKK" },
            { "@type": "Offer", name: "Timepris 22:00–7:00 hverdage", price: "1743.75", priceCurrency: "DKK" },
            { "@type": "Offer", name: "Akut udrykning weekend/helligdage (1. time)", price: "2743.75", priceCurrency: "DKK" },
          ],
          termsOfService: "Garanti + TEKNIQ Garanti op til 150.000 kr.",
        }),
      },
    ],
  }),
  component: PrivatePrisPage,
});

const normalRows = [
  { time: "7:00 – 15:00 på hverdage", price: "Kr. 868,75,-", vat: "inkl. moms" },
  { time: "15:00 – 22:00 på hverdage", price: "Kr. 1368,75,-", vat: "inkl. moms" },
  { time: "22:00 – 7:00 på hverdage", price: "Kr. 1743,75,-", vat: "inkl. moms" },
];

const extraRows = [
  { time: "15:00 – 20:00 på hverdage, inkl. 1 time", price: "Kr. 1868,75,-", vat: "inkl. moms" },
  { time: "Efterfølgende time(r)", price: "Kr. 1368,75,-", vat: "inkl. moms" },
  { time: "20:00 – 7:00 på hverdage, weekender & helligdage, inkl. 1 time", price: "Kr. 2743,75,-", vat: "inkl. moms" },
  { time: "Efterfølgende time(r)", price: "Kr. 1743,75,-", vat: "inkl. moms" },
];

function PrivatePrisPage() {
  return (
    <SiteShell>
      <Breadcrumbs items={[{ label: "Forside", to: "/" }, { label: "Priser privat" }]} />
      <PageHero
        variant="compact"
        accentColor="oklch(0.72 0.16 130)"
        eyebrow="Priser · Privat · inkl. moms"
        title="Priser for privatkunder, faste, lave og gennemskuelige"
        intro="Vi tror på at en god elektriker også er en gennemskuelig elektriker. Her er hele vores prisliste for privatkunder, inkl. moms, uden skjulte gebyrer og uden små bogstaver."
      />
      <Section>
        <Prose>
          <p>
            Vi fokuserer på høj kvalitet til konkurrencedygtige priser. SAK-Electric A/S
            (en del af Elektriker-Elektriker) bestræber sig på at have faste, lave, fordelagtige og
            gennemskuelige timepriser. Vi har ét enkelt formål med vores prismodel: at holde vores
            kunders omkostninger nede uden at gå på kompromis med kvaliteten, dokumentationen eller
            garantien.
          </p>
          <p>
            Ved indgåelse af serviceaftale kan nedenstående priser forhandles. <strong>Alle priser
            er inkl. moms og gælder for privatkunder.</strong>{" "}
            <Link to="/priser-erhverv" className="inline-flex h-11 items-center rounded-md border border-border bg-background px-5 text-sm font-semibold text-[var(--ink)] hover:bg-secondary">Er du erhverv?</Link>
          </p>
        </Prose>

        <div className="mt-10">
          <h2 className="text-2xl font-semibold text-[var(--ink)]">Timepriser inden for normal arbejdstid</h2>
          <p className="mt-2 text-sm text-muted-foreground">Pris pr. montørtime</p>
          <div className="mt-4 overflow-hidden rounded-2xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-secondary">
                <tr>
                  <th className="px-5 py-3 font-semibold text-[var(--ink)]">Tidsrum</th>
                  <th className="px-5 py-3 text-right font-semibold text-[var(--ink)]">Pris</th>
                </tr>
              </thead>
              <tbody>
                {normalRows.map((r, i) => (
                  <tr key={r.time} className={i % 2 === 0 ? "bg-card" : "bg-background"}>
                    <td className="px-5 py-3 text-[var(--ink)]/85">{r.time}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="font-semibold text-[var(--ink)]">{r.price}</div>
                      <div className="text-xs text-muted-foreground">{r.vat}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Bemærk der afsættes 30 min til kørsel.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Der kan til enhver tid rekvireres specialteknikere mod en merpris ud over ovenstående til kr. 498,75,- pr. time. (Ved f.eks. IHC programmering, data programmering, avanceret fejlfinding og dørtelefoni)
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Ved akut hjælp og assistance fra vores døgnvagtsservice, kan vi hjælpe dig inden for få timer. Vi står klar døgnet rundt!
          </p>
        </div>

        <div className="mt-10">
          <h2 className="text-2xl font-semibold text-[var(--ink)]">Timepriser uden for normal arbejdstid</h2>
          <p className="mt-2 text-sm text-muted-foreground">Pris pr. montørtime</p>
          <div className="mt-4 overflow-hidden rounded-2xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-secondary">
                <tr>
                  <th className="px-5 py-3 font-semibold text-[var(--ink)]">Tidsrum</th>
                  <th className="px-5 py-3 text-right font-semibold text-[var(--ink)]">Pris</th>
                </tr>
              </thead>
              <tbody>
                {extraRows.map((r, i) => (
                  <tr key={r.time + i} className={i % 2 === 0 ? "bg-card" : "bg-background"}>
                    <td className="px-5 py-3 text-[var(--ink)]/85">{r.time}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="font-semibold text-[var(--ink)]">{r.price}</div>
                      <div className="text-xs text-muted-foreground">{r.vat}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Priserne er inkl. moms. NB. Mindste faktureringstid er minimum 1 time. Efterfølgende faktureres pr. påbegyndt time.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Aflysning bedes meldes inden 24 timer før aftalt ankomst pr. mail, for at undgå aflysningsgebyr.
          </p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-[var(--ink)]">Servicevogn</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Faktureres med: Kr. 123,75,- inkl. moms pr. time, uanset tidspunkt pr. medarbejder.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Der kan indgås særaftaler herom ved større entreprisesager.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-[var(--ink)]">Miljøtillæg</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Kr. 82,- inkl. moms.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="text-lg font-semibold text-[var(--ink)]">KLS test og dokumentation</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Som et led i din garanti for at det udførte arbejde er sikkerhedsmæssigt korrekt udført, skal der ved hver opgave udarbejdes en lovpligtig KLS rapport.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Denne rapport koster Kr. 243,75,- inkl. moms.
            </p>
          </div>
        </div>

        <div className="mt-10 rounded-2xl border border-border bg-secondary/40 p-6">
          <p className="text-muted-foreground">
            Vi er til enhver tid også leveringsdygtige i et uforpligtende tilbud efter dine ønsker.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/tilbud" className="inline-flex h-11 items-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:brightness-95">
              Få uforpligtende tilbud
            </Link>
            <a href="tel:+4570400070" className="inline-flex h-11 items-center rounded-md border border-border bg-background px-5 text-sm font-semibold text-[var(--ink)] hover:bg-secondary">
              Ring +45 70 4000 70
            </a>
          </div>
        </div>
      </Section>
      <CTA />
      <div className="h-10" />
    </SiteShell>
  );
}
